import React, { useState } from "react";
import * as XLSX from "xlsx-js-style";
import { useUser } from "../../hooks/useUser";
import { useCity } from "../../hooks/useCity";
import { useCurrentUser } from "../../hooks/useAuth";
import { useEventType } from "../../hooks/useEventType";
import eventService from "../../services/eventService";
import { toast } from "react-toastify";
import { EventStatusConstants } from "../../constants/eventStatus";
import divisionsService from "../../services/divisionService";
import externalSourcesService from "../../services/externalSourcesService";
import activityTypeService from "../../services/activityTypeService";
import countryService from "../../services/countryService";
import spendTypeService from "../../services/spendTypeService";
import relationshipService from "../../services/relationshipService";
import { formatDateYYYYMMDD, getCurrencyCode } from "../../utils/helpers";
import organizationService from "../../services/organizationService";

function TovReport({ filters, selectedEvents }) {
  const user = useCurrentUser();
  const [showSpinning, setShowSpinning] = useState(false);
  const { data: usersData } = useUser();
  const { data: eventTypesData } = useEventType();
  const { data: cityData } = useCity();

  const filterEvents = (events) => {
    if (!events) return [];

    return events.filter((event) => {
      // Only include selected events
      if (selectedEvents.length > 0 && !selectedEvents.includes(event.id)) {
        return false;
      }

      // Date Range Filter
      if (
        filters.dateRange.from &&
        new Date(event.start_date) < new Date(filters.dateRange.from)
      )
        return false;
      if (
        filters.dateRange.to &&
        new Date(event.start_date) > new Date(filters.dateRange.to)
      )
        return false;

      // Event Type Filter
      if (
        filters.selectedEventType &&
        event.event_type_id !== filters.selectedEventType
      )
        return false;

      // Division Filter
      if (
        filters.selectedDivision &&
        event.division_id !== filters.selectedDivision
      )
        return false;

      // Team Filter
      if (filters.selectedTeam && event.team_id !== filters.selectedTeam)
        return false;

      // Spend Type Filter
      if (
        filters.selectedSpendType &&
        event.spend_type_id !== filters.selectedSpendType
      )
        return false;

      // Activity Type Filter
      if (
        filters.selectedActivityType &&
        event.activity_type_id !== filters.selectedActivityType
      )
        return false;

      // Status Filter
      if (
        filters.selectedStatus &&
        event.status?.toLowerCase() !== filters.selectedStatus.toLowerCase()
      )
        return false;

      // Search Query Filter
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        const matchesSearch =
          event.id?.toString().toLowerCase().includes(searchLower) ||
          event.promo_number?.toLowerCase().includes(searchLower) ||
          event.activity_type_id
            ?.toString()
            .toLowerCase()
            .includes(searchLower) ||
          event.division_id?.toString().toLowerCase().includes(searchLower) ||
          event.external_source_id
            ?.toString()
            .toLowerCase()
            .includes(searchLower) ||
          event.relationship_id
            ?.toString()
            .toLowerCase()
            .includes(searchLower) ||
          event.spend_type_id?.toString().toLowerCase().includes(searchLower) ||
          event.currency?.toLowerCase().includes(searchLower) ||
          event.name?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      return true;
    });
  };

  const divisionNames = {};
  const externalSourceNames = {};
  const activityNames = {};
  const countryNames = {};
  const spendTypeNames = {};
  const relationshipNames = {};
  const organizationData = {};

  const getCached = async (cache, service, id) => {
    if (!id) return "";
    if (!cache[id]) cache[id] = await service.getNameById(id);
    return cache[id];
  };
  const getCachedDataById = async (cache, service, id) => {
    if (!id) return null;
    if (!cache[id]) cache[id] = await service.getById(id);
    return cache[id];
  };

  const handleExport = async () => {
    try {
      setShowSpinning(true);
      const headers = [
        "Record Add/Modify Notes",
        "GTR Key Assigned by System",
        "Starid",
        "Data Collector Email",
        "Employee Contact UPI",
        "Expected Report Period",
        "Division",
        "External Source System/Process",
        "Promo Number",
        "Event Number",
        "Event Name",
        "Activity Name",
        "Activity Start Date",
        "Activity End Date",
        "Activity Country",
        "Activity---Expense Type (Spend Type)",
        "Payment Date",
        "TOV Amount attributable to this recipient",
        "Total Grant Amount",
        "Amount Currency",
        "Expense Description",
        "Indirect/Direct relationship for this recipient",
        "APO ID",
        "Recipient Type",
        "Recipient Hcp ID Professional Classification Number",
        "Recipient HCP First Name",
        "Recipient HCP Last Name",
        "Organization Name",
        "Address City",
        "Recipient (HCP Contact) Registration Number",
        "Recipient (HCP Contact) First Name",
        "Recipient (HCP Contact) Last Name",
        "Email of Requestor",
        "Program",
        "Ignore",
        "Set to End",
      ];
      const eventData =
        await eventService.getEventsDataWithEveryEventTheirAttendees();
      const filteredEvents = filterEvents(eventData);

      // Filter events for both Completed and Closed status
      const exportableEvents = filteredEvents.filter((event) => {
        if (!event.status) return false;
        const status = event.status.trim();
        const isCompleted = status === EventStatusConstants.COMPLETED.text;
        const isClosed = status === EventStatusConstants.CLOSED.text;

        return isCompleted || isClosed;
      });

      if (exportableEvents.length === 0) {
        toast.info("No completed or closed events found to export.");
        return;
      }

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([headers]);

      if (exportableEvents.length > 0) {
        const rows = [];

        for (const event of exportableEvents) {
          const spendPerAttendee = event.spend_value
            ? parseFloat(event.spend_value) / (event.attendees?.length || 1)
            : 0;

          const emailUser =
            usersData.find((user) => user.id === event.created_by)
              ?.email_address || "";
          const upiUser =
            usersData.find((user) => user.id === event.created_by)?.UPI_num ||
            "";

          const splitYear = event.start_date
            ? new Date(event.start_date).getFullYear().toString()
            : "";

          const divisionName = await getCached(
            divisionNames,
            divisionsService,
            event.division_id
          );
          const externalSourceName = await getCached(
            externalSourceNames,
            externalSourcesService,
            event.external_source_id
          );
          const activityName = await getCached(
            activityNames,
            activityTypeService,
            event.activity_type_id
          );
          const countryName = await getCached(
            countryNames,
            countryService,
            event.country_id
          );
          const spendTypeName = await getCached(
            spendTypeNames,
            spendTypeService,
            event.spend_type_id
          );
          const relationName = await getCached(
            relationshipNames,
            relationshipService,
            event.relationship_id
          );
          const organization = await getCachedDataById(
            organizationData,
            organizationService,
            event.organization_id
          );

          const currencyCode = getCurrencyCode(event.currency);

          const eventTypeName = eventTypesData?.find(
            (type) => type.id === event.event_type_id
          )?.needs_organization
            ? "Organization"
            : "Individual";

          const filteredAttendees = event.attendees || [];

          if (filteredAttendees.length === 0) {
            rows.push([
              "",
              "",
              "",
              emailUser,
              upiUser,
              splitYear,
              divisionName,
              externalSourceName,
              event.promo_number || "",
              event.id || "",
              event.name || "",
              spendTypeName,
              formatDateYYYYMMDD(event.start_date) || "",
              formatDateYYYYMMDD(event.end_date) || "",
              countryName,
              activityName,
              formatDateYYYYMMDD(event.start_date) || "",
              "",
              event.spend_value || "",
              currencyCode || "",
              spendTypeName,
              relationName,
              organization?.APO_number || "",
              eventTypeName,
              organization?.organization_id || "",
              "",
              "",
              organization?.name || "",
              cityData.find((city) => city.id === organization?.city_id)
                ?.name || "",
              organization?.registration_number || "",
              organization?.contact_name || "",
              organization?.contact_name || "",
              user?.email_address || "",
              "SAU-Base",
              "",
              "",
            ]);
          } else {
            for (const attendee of filteredAttendees) {
              rows.push([
                "",
                "",
                "",
                emailUser,
                upiUser,
                splitYear,
                divisionName,
                externalSourceName,
                event.promo_number || "",
                event.id || "",
                event.name || "",
                spendTypeName,
                formatDateYYYYMMDD(event.start_date) || "",
                formatDateYYYYMMDD(event.end_date) || "",
                countryName,
                activityName,
                formatDateYYYYMMDD(event.start_date) || "",
                spendPerAttendee || "",
                event.spend_value || "",
                currencyCode || "",
                spendTypeName,
                relationName,
                attendee.APO_number || "",
                eventTypeName,
                attendee.medical_license || "",
                attendee.first_name || "",
                attendee.family_name || "",
                attendee.organization || "",
                cityData.find((city) => city.id === attendee.city_id)?.name ||
                  "",
                "",
                "",
                "",
                user?.email_address || "",
                "SAU-Base",
                "",
                "",
              ]);
            }
          }
        }

        XLSX.utils.sheet_add_aoa(worksheet, rows, { origin: "A2" });
      }

      if (exportableEvents.length === 0) {
        toast.info("No completed or closed events found to export.");
        return;
      }
      // Append data rows to worksheet

      const nonExportableEvents = filteredEvents.filter(
        (event) =>
          event.status !== EventStatusConstants.COMPLETED.text &&
          event.status !== EventStatusConstants.CLOSED.text
      );
      if (nonExportableEvents.length > 0) {
        const nonExportableIds = nonExportableEvents
          .map((event) => event.id)
          .join(", ");
        toast.info(
          `The following events were not exported because they are not Completed or Closed: ${nonExportableIds}`
        );
      }

      // Set column widths and styles
      const columnWidths = headers.map(() => ({ wch: 33 }));
      worksheet["!cols"] = columnWidths;

      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!worksheet[address]) {
            worksheet[address] = { v: "", t: "s" }; // Create cell if it doesn't exist
          }
          worksheet[address].s = {
            font: R === 0 ? { bold: true, sz: 12 } : { sz: 11 },
            alignment: {
              horizontal: "center",
              vertical: "center",
              wrapText: true,
            },
            fill: R === 0 ? { fgColor: { rgb: "a5cea8" } } : undefined,
          };
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "TOV Report");

      // Generate timestamp for filename
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, "-").slice(0, 19); // Format: YYYY-MM-DDTHH-MM-SS
      const filename = `TOV_Report_${timestamp}.xlsx`;

      XLSX.writeFile(workbook, filename);

      // Update status of exported events to Closed (only for Completed events)
      const completedEvents = exportableEvents.filter(
        (event) => event.status === "Completed"
      );

      if (completedEvents.length > 0) {
        await Promise.all(
          completedEvents.map(async (event) => {
            try {
              await eventService.edit({
                id: event.id.toString(),
                data: {
                  status: EventStatusConstants.CLOSED.text,
                  tov_exported: true,
                },
              });
            } catch (error) {
              console.error(
                `Failed to update status for event ${event.id}:`,
                error
              );
            }
          })
        );
      }
      toast.success(
        `TOV Report exported successfully! ${exportableEvents.length} events included.`
      );
    } catch (error) {
      toast.error("Failed to generate TOV Report. Please try again.");
    } finally {
      setShowSpinning(false);
    }
  };

  return (
    <button
      data-track="events-tov-report-button"
      data-module="Events"
      onClick={handleExport}
      disabled={showSpinning}
      className={`bg-green-600 transition-colors duration-200 px-4 py-2 uppercase 
        rounded-lg text-white font-medium flex gap-2 items-center ${
          showSpinning
            ? "cursor-not-allowed bg-green-600/60"
            : "hover:bg-green-700"
        }`}
    >
      <i className="fa-solid fa-file-arrow-down text-lg"></i>
      <span className="text-sm">TOV Report</span>
      {showSpinning && (
        <svg
          className="animate-spin h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
    </button>
  );
}

export default TovReport;